import React, { useMemo } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
} from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useTheme } from '../../theme';
import { fs, iconSize, s, v } from '../../utils/libs';

interface PickerFooterButtonProps {
  /** What the button does, in bold: "Save Order", "Send STK Push". */
  label: string;
  /** After a "·" in regular weight: the customer, the amount. */
  detail?: string;
  disabled?: boolean;
  /** A spinner in place of the text. */
  loading?: boolean;
  onPress: () => void;
  accessibilityLabel?: string;
  /** Corner radius; the checkout's payment footers use 8 like the app's buttons. */
  borderRadius?: number;
  /** An arrow after the label, for a button that moves to the next step. */
  showArrow?: boolean;
}

/** The customer picker's main button, under the staff section. */
const PickerFooterButton: React.FC<PickerFooterButtonProps> = ({
  label,
  detail,
  disabled = false,
  loading = false,
  onPress,
  accessibilityLabel,
  borderRadius,
  showArrow = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <Pressable
      onPress={() => {
        if (!disabled) onPress();
      }}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ disabled, busy: loading }}
      accessibilityLabel={
        accessibilityLabel ?? (detail ? `${label} · ${detail}` : label)
      }
      style={({ pressed }) => [
        styles.button,
        borderRadius !== undefined && { borderRadius },
        disabled && styles.buttonDisabled,
        pressed && !disabled && styles.buttonPressed,
      ]}>
      {loading ? (
        <ActivityIndicator size="small" color={theme.colors.textInverse} />
      ) : (
        <>
          <Text
            style={[styles.label, disabled && styles.labelDisabled]}
            numberOfLines={1}>
            {label}
            {detail ? <Text style={styles.detail}> · {detail}</Text> : null}
          </Text>
          {showArrow && !disabled ? (
            <MaterialCommunityIcons
              name="arrow-right"
              size={iconSize(18)}
              color={theme.colors.textInverse}
            />
          ) : null}
        </>
      )}
    </Pressable>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    button: {
      height: v(50),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.success,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s(8),
      paddingHorizontal: s(16),
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    buttonDisabled: {
      backgroundColor: theme.colors.border,
    },
    buttonPressed: {
      opacity: 0.85,
    },
    // Shrinks so a long name still cuts off inside the button row
    label: {
      flexShrink: 1,
      fontFamily: GTFonts.bold,
      fontSize: fs(15),
      lineHeight: fs(19),
      color: theme.colors.textInverse,
    },
    labelDisabled: {
      color: theme.colors.graphiteDark,
    },
    detail: {
      fontFamily: GTFonts.regular,
      opacity: 0.9,
    },
  });

export default PickerFooterButton;
